// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import { connect } from 'react-redux';
import { Query } from 'urql';
import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import { NativeBridge } from '@ta/common.webview';
import { Opener } from '@ta/overlays.managers';
import * as Import from '@ta/platform.import';
import { ProfileQueryWithUserId } from '@ta/social.feed';
import { SocialLoginGate } from '@ta/social.login-gate';
import type { RepostStatus, ExternalRepostProps } from '@ta/social.types';
import { Icon } from '@ta/styleguide.icon';
import { InteractionLogger } from '@ta/tracking.interactions';
import type { RepostUploaderProps } from '@ta/social.repost-uploader';
import RepostToast from './RepostToast';
import styles from './RepostAction.less';
import useUpdatedRepostStats from './useUpdatedRepostStats';

const [trigger, RepostUploader] = Import.component.onTrigger<_, RepostUploaderProps>(
  () => import('@ta/social.repost-uploader'),
  'RepostUploader',
);

type Props = {|
  loggedInUserId: ?string,
  ...ExternalRepostProps,
|};

type RepostToastCallback = (
  repostContentType: string,
  repostStatus: RepostStatus,
  username?: string,
) => void;

const checkPermissionForUser = user => {
  if (user && user.blockStatus) {
    const { blockStatus } = user;
    if (blockStatus && (blockStatus.isBlocking || blockStatus.isBlockingYou)) {
      return false;
    }
  }
  return true;
};

const RepostAction = (props: Props) => {
  const { current: name } = React.useRef(`RepostUploader:${Math.random()}`);
  const { feedSectionItem } = props.repostPreviewProps;
  const repostObjectId =
    props.originalRepostReference?.id ||
    feedSectionItem.object.id ||
    feedSectionItem.object.mediaBatchId ||
    feedSectionItem.object.postId;
  const repostType = props.originalRepostReference?.type || feedSectionItem.object.__typename;
  const updatedRepostStats = useUpdatedRepostStats(repostObjectId, repostType);
  const isActive = updatedRepostStats
    ? updatedRepostStats.isReposted
    : props.socialStatistics && props.socialStatistics.isReposted;

  const { styleAsShareListItem, openRepostModalAsLocal } = props;
  const repostToast = React.useRef<?RepostToastCallback>(undefined);

  const repostCallback = (contentType: string, repostStatus: RepostStatus, username?: string) => {
    if (repostToast.current) {
      repostToast.current(contentType, repostStatus, username);
    }
  };

  /** If a user blocks or is blocking the author of the reposted content, then return early on the client */
  const checkPermissions = () => {
    if (
      checkPermissionForUser(props.actor) &&
      (props.repostPreviewProps ? checkPermissionForUser(props.repostPreviewProps.actor) : true)
    ) {
      return true;
    }

    if (typeof repostToast.current === 'function') {
      repostToast.current(repostType, 'no_permission');
    }
    return false;
  };

  let repostUploader;
  const { loggedInUserId, ...copiedProps } = props;
  if (!props.repostPreviewProps.member && loggedInUserId) {
    const userIds = [{ id: props.loggedInUserId }];
    repostUploader = (
      <Query query={ProfileQueryWithUserId} variables={{ userIds }}>
        {({ data }) => {
          const { memberProfiles = null } = data || {};
          const member = memberProfiles ? memberProfiles[0] : null;
          const copiedPreviewProps = { ...props.repostPreviewProps };

          copiedPreviewProps.member = member;
          copiedProps.repostPreviewProps = copiedPreviewProps;

          return (
            <RepostUploader {...copiedProps} modalName={name} repostCallback={repostCallback} />
          );
        }}
      </Query>
    );
  } else {
    repostUploader = (
      <RepostUploader {...copiedProps} modalName={name} repostCallback={repostCallback} />
    );
  }
  const action = (
    <React.Fragment>
      <SocialLoginGate pid={40434}>
        {loginGate => (
          <Opener name={name} local={openRepostModalAsLocal}>
            {open => (
              <InteractionLogger>
                {logRepost => (
                  <span
                    onClick={e => {
                      if (isActive) {
                        return;
                      }
                      trigger();

                      if (props.onClick) {
                        props.onClick();
                      }

                      if (NativeBridge.isNativeWebview()) {
                        if (repostObjectId && repostType) {
                          NativeBridge.postToBridge('repost', 1.0, {
                            objectId: repostObjectId,
                            type: repostType,
                          });
                        }
                      } else {
                        logRepost(e, 'repost');
                        loginGate()
                          .then(checkPermissions)
                          .then(permission => {
                            if (permission) {
                              open();
                            }
                          });
                      }
                    }}
                    className={classNames(
                      styleAsShareListItem ? styles.share_list_item : styles.social_action,
                      { [styles.active]: isActive },
                    )}
                  >
                    <Icon name="repost" className={styles.icon} />
                    <span className={`ui_link ${styles.text} socialCTA`}>
                      {/* Reposted / Repost on TripAdvisor / Repost */}
                      {/* eslint-disable no-nested-ternary */}
                      {isActive
                        ? localize('reposted')
                        : styleAsShareListItem
                        ? localize('social_repost_on_tripadvisor')
                        : localize('social_repost')}
                    </span>
                  </span>
                )}
              </InteractionLogger>
            )}
          </Opener>
        )}
      </SocialLoginGate>

      {repostUploader}

      {/* $FlowFixMe92 */}
      <RepostToast>
        {launchRepostToast => {
          repostToast.current = launchRepostToast;
        }}
      </RepostToast>
    </React.Fragment>
  );

  return <React.Fragment>{featureIsEnabled('social_reposts') ? action : null}</React.Fragment>;
};

export default connect<Props, ExternalRepostProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
}))(RepostAction);
